import { ChangeDetectorRef, Directive, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportViewService } from '@app/main/report-view/servicies/report-view.service';
import { MainViewDirective } from '@shared/directives';
import { IReport, ResponsibleType } from '@shared/interfaces';
import { IQueryReport } from '@shared/interfaces/queries/IQueryReport';
import { CacheService, GuardService, NavigationService, ReportService } from '@shared/services';
import { Location } from '@angular/common';
import { ReportGroupByOption, SortByOption } from '@shared/enums';

@Directive()
export class ReportViewDirective extends MainViewDirective<IReport> implements OnInit {
    constructor(
        protected guardService: GuardService,
        protected changeDetection: ChangeDetectorRef,
        protected cacheService: CacheService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected location: Location,
        protected navigationService: NavigationService,
        protected reportViewService: ReportViewService,
        protected reportService: ReportService
    ) {
        super(guardService, changeDetection, cacheService, router, route, location, navigationService);
        this.type = 'reports';
        this.selectedGroupByOption = ReportGroupByOption.responsible;
    }

    public async ngOnInit(): Promise<void> {
        this.reportingPeriod = this.reportingPeriods[0];
        await super.ngOnInit();
    }

    protected async loadData(): Promise<void> {
        const query: IQueryReport = {
            reportingPeriod: this.reportingPeriod,
            responsibleType: ResponsibleType.all,
            withResponsible: true
        };

        const result = await this.reportService.getMyItems(query);
        this.data = this.items = result;
    }

    protected async openReport(report: IReport): Promise<void> {
        await this.router.navigate(['/report', report.id], {
            queryParamsHandling: 'merge'
        });
    }

    protected get groupByOption(): typeof ReportGroupByOption {
        return ReportGroupByOption;
    }

    protected group(groupBy: ReportGroupByOption): void {
        this.groupedItems = {};
        switch (groupBy) {
            case ReportGroupByOption.responsible: {
                for (const responsibleType of [ResponsibleType.sag, ResponsibleType.rc]) {
                    const reports = this.items.filter(report => report.responsible.responsibleType === responsibleType);
                    if (reports.length > 0) {
                        this.groupedItems[responsibleType] = this.breakDownIntoLevels(reports);
                    }
                }
                this.selectedGroupByOption = ReportGroupByOption.responsible;
                break;
            }
            case ReportGroupByOption.status:
                this.groupedItems = this.items.reduce((acc, item) => {
                    const status = item.reportStatus;
                    if (!acc[status]) {
                        acc[status] = [];
                    }
                    acc[status].push(item);
                    return acc;
                }, {});
                this.selectedGroupByOption = ReportGroupByOption.status;
                break;
            case ReportGroupByOption.none:
                this.groupedItems = {
                    all: this.items
                };
                this.selectedGroupByOption = ReportGroupByOption.none;
                break;
            default:
                console.error('Unknown groupBy option:', groupBy);
        }
        this.changeDetection.detectChanges();
    }

    /**
     * Breaks down the items into levels based on their responsible's orgLevel.
     *
     * @param {IReport} reports The reports to be broken down.
     * @returns {IReport[][]} An array of arrays, where each sub-array contains reports at the same orgLevel.
     */
    private breakDownIntoLevels(reports: IReport[]): IReport[][] {
        return reports.reduce((acc, item) => {
            const level = item.responsible.orgLevel || 0;
            if (!acc[level]) {
                acc[level] = [];
            }
            acc[level].push(item);
            return acc;
        }, []);
    }

    public get pageSizes(): number[] {
        return window.innerWidth >= 900 && window.innerWidth < 1248
            ? [3, 6, 9, 12, 15, 18, 21, 24, 27, 30]
            : [4, 8, 12, 16, 20, 24, 28, 32, 36, 40];
    }

    public get pageSize(): number {
        return window.innerWidth >= 900 && window.innerWidth < 1248 ? 9 : 12;
    }

    protected get sortByOptions(): { value: SortByOption; direction: 'asc' | 'desc' }[] {
        return Object.values(SortByOption).flatMap(option => [
            { value: option as SortByOption, direction: 'asc' },
            { value: option as SortByOption, direction: 'desc' }
        ]);
    }
}
